/**
 * Deterministic gradient avatar for an address, so the same wallet always
 * looks the same across the header, drawer and portfolio.
 */
import { memo, useMemo } from 'react';
import { cn } from '@/lib/cn';

function hashToHues(address: string): [number, number] {
  let h = 0;
  for (let i = 0; i < address.length; i++) {
    h = (h * 31 + address.charCodeAt(i)) >>> 0;
  }
  // Keep both hues in the violet → magenta → blue band so avatars sit on-brand.
  const a = 250 + (h % 70);
  const b = 190 + ((h >>> 8) % 90);
  return [a, b];
}

export interface AddressAvatarProps {
  address: string;
  size?: number;
  className?: string;
}

export const AddressAvatar = memo(function AddressAvatar({
  address,
  size = 20,
  className,
}: AddressAvatarProps) {
  const background = useMemo(() => {
    const [a, b] = hashToHues(address.toLowerCase());
    return `radial-gradient(circle at 30% 25%, hsl(${a} 85% 72%), hsl(${b} 70% 45%) 70%)`;
  }, [address]);

  return (
    <span
      aria-hidden="true"
      className={cn('inline-block shrink-0 rounded-full ring-1 ring-white/20', className)}
      style={{ width: size, height: size, background }}
    />
  );
});
